import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const locations = [
  {
    key: 'longpont',
    name: 'Longpont-sur-Orge',
    zip: '91310',
    accent: '#0205B3',
    accentClass: 'text-[#0205B3]',
    bgClass: 'bg-[#0205B3]',
    btnClass: 'bg-[#0205B3] hover:bg-[#0205B3]/90',
    tintBg: 'bg-blue-100',
    borderClass: 'border-[#0205B3]',
    address: ['Intermarché', 'Galerie marchande - Zac des Échassons', '91310 Longpont-sur-Orge'],
    phone: '01 69 80 73 75',
    phoneTel: '0169807375',
    email: 'pressingaquabluenet@gmail.com',
    hours: '10h00 - 19h00',
    mapEmbed: 'https://www.google.com/maps?q=Intermarché+SUPER+Longpont-sur-Orge,+ZAC+des+Échassons,+91310&output=embed',
    mapExternal: 'https://www.google.com/maps?q=Intermarché+Longpont-sur-Orge+91310',
    mapTitle: 'Localisation PRESSING AQUABLUENET Longpont-sur-Orge'
  },
  {
    key: 'mennecy',
    name: 'Mennecy',
    zip: '91540',
    accent: '#16a34a',
    accentClass: 'text-green-600',
    bgClass: 'bg-green-600',
    btnClass: 'bg-green-600 hover:bg-green-700',
    tintBg: 'bg-green-100',
    borderClass: 'border-green-600',
    address: ['Intermarché', 'Rue Jean Cocteau', '91540 Mennecy'],
    phone: '09 84 50 99 42',
    phoneTel: '0984509942',
    email: 'pressingaquabluenet@gmail.com',
    hours: '9h30 - 19h00',
    mapEmbed: 'https://www.google.com/maps?q=Intermarché+Rue+Jean+Cocteau+Mennecy+91540&output=embed',
    mapExternal: 'https://www.google.com/maps?q=Intermarché+Rue+Jean+Cocteau+Mennecy+91540',
    mapTitle: 'Localisation PRESSING AQUABLUENET Mennecy'
  }
];

const LocationContactCard = ({ loc }) => (
  <Card className={`border-2 ${loc.borderClass} shadow-xl overflow-hidden`} data-testid={`contact-card-${loc.key}`}>
    <CardContent className="p-0">
      <div className={`${loc.bgClass} text-white p-6 text-center`}>
        <h3 className="text-2xl font-bold">{loc.name}</h3>
        <p className="text-white/90 font-semibold mt-1">{loc.zip}</p>
      </div>

      <div className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className={`${loc.tintBg} rounded-full p-2 flex-shrink-0`}>
            <MapPin className={`h-5 w-5 ${loc.accentClass}`} />
          </div>
          <div>
            <p className="font-semibold text-gray-900 mb-0.5">Adresse</p>
            <p className="text-gray-600 text-sm">
              {loc.address.map((line) => (
                <span key={line}>{line}<br /></span>
              ))}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className={`${loc.tintBg} rounded-full p-2 flex-shrink-0`}>
            <Phone className={`h-5 w-5 ${loc.accentClass}`} />
          </div>
          <div>
            <p className="font-semibold text-gray-900 mb-0.5">Téléphone</p>
            <a href={`tel:${loc.phoneTel}`} className={`${loc.accentClass} hover:underline font-medium`} data-testid={`contact-${loc.key}-phone`}>
              {loc.phone}
            </a>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className={`${loc.tintBg} rounded-full p-2 flex-shrink-0`}>
            <Mail className={`h-5 w-5 ${loc.accentClass}`} />
          </div>
          <div>
            <p className="font-semibold text-gray-900 mb-0.5">Email</p>
            <a href={`mailto:${loc.email}`} className={`${loc.accentClass} hover:underline text-sm break-all`} data-testid={`contact-${loc.key}-email`}>
              {loc.email}
            </a>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className={`${loc.tintBg} rounded-full p-2 flex-shrink-0`}>
            <Clock className={`h-5 w-5 ${loc.accentClass}`} />
          </div>
          <div>
            <p className="font-semibold text-gray-900 mb-0.5">Horaires</p>
            <p className="text-gray-600 text-sm">
              <strong>Mardi - Samedi</strong><br />
              {loc.hours}
            </p>
            <p className="text-xs text-red-600 font-medium mt-0.5">Fermé Dimanche et Lundi</p>
          </div>
        </div>

        <div className="rounded-xl overflow-hidden h-56 border border-gray-200">
          <iframe
            src={loc.mapEmbed}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={loc.mapTitle}
          ></iframe>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button
            className={`${loc.btnClass} text-white`}
            onClick={() => { window.location.href = `tel:${loc.phoneTel}`; }}
            data-testid={`contact-${loc.key}-call-btn`}
          >
            <Phone className="mr-2 h-4 w-4" />
            Appeler
          </Button>
          <Button
            variant="outline"
            className={`border-2 ${loc.borderClass} ${loc.accentClass} hover:bg-gray-50`}
            onClick={() => window.open(loc.mapExternal, '_blank')}
            data-testid={`contact-${loc.key}-map-btn`}
          >
            <MapPin className="mr-2 h-4 w-4" />
            Itinéraire
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const ContactSection = () => {
  return (
    <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-[#0205B3] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <MapPin className="h-4 w-4" />
            <span>Nous contacter</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Quel pressing souhaitez-vous contacter ?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choisissez l'établissement AQUABLUENET le plus proche de vous : <strong>Longpont-sur-Orge</strong> ou <strong>Mennecy</strong>.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {locations.map((loc) => (
            <LocationContactCard key={loc.key} loc={loc} />
          ))}
        </div>
      </div>
    </section>
  );
};
